<template>
  <cv-form @submit.prevent="handleSave">
    <div v-if="!inline" class="name" :class="{ 'learning-fa': lfaAppName }">{{ ownerName }}</div>
    <dl class="field-set annotation-notes-content">
      <dd class="field-input">
        <RichTextEditor
          :key="editor.key"
          :kind="editor.kind"
          :size="editor.size"
          :config="richTextEditorConfig"
          :disabled="isInputDisabled"
          :initial-content="note"
          :initial-option-type="initialOptionType"
          @change="handleNotesChange"
        />
      </dd>
    </dl>
    <dl v-if="!inline && !isEditorContext" class="field-set type-tag">
      <dt class="field-label">
        {{ t("settings_label_keywords") }}
        <cv-tooltip
          v-if="showAutoCompleteKeywordTag"
          class="field-tooltip"
          :tip="t('error_tag_tooltip_message')"
        >
          <SvgIcon name="Info24" kind="action" />
        </cv-tooltip>
      </dt>
      <dd class="field-input">
        <Keywords
          v-if="!showAutoCompleteKeywordTag"
          :key="editor.key"
          :tags="keywordsRef"
          :placeholder="keywordsPlaceholder"
          :character-limit="250"
          :delimiter="','"
          mode="EDIT"
          :message="task"
          :disabled="isInputDisabled"
          :text-id="uuid"
          @keydown.enter.prevent
          @input="handleKeywordsChange"
          @remove="handleKeywordsChange"
          @executed="execution = $event"
        />
        <EditableTagListAutoComplete
          v-else
          :single-selection="false"
          :current-selection="selectedItems"
          :items="keywordLists"
          :placeholder="autoCompletePlaceholder"
          :message="task"
          :tags="keywordsRef"
          :character-limit="250"
          @change="handleKeywordTagChange"
          @executed="execution = $event"
        />
      </dd>
    </dl>
    <dl v-if="showSharingControls" class="field-set visible-to-students">
      <dd class="field-input">
        {{ t("annotation_visible_to_students_label") }}
        <cv-tooltip
          class="field-tooltip"
          direction="bottom"
          alignment="start"
          :tip="t('annotation_visibility_tooltip_text')"
        >
          <SvgIcon name="Info24" kind="action" />
        </cv-tooltip>
        <cv-toggle
          class="toggle"
          small="small"
          value="student-visible-toggle"
          hide-label
          :checked="visibleByStudentsToggleValue"
          :disabled="isInputDisabled"
          @change="handleToggleVisibleByStudents"
        >
          <template #text-left>{{ t("annotation_visible_to_students_off_label") }}</template>
          <template #text-right>{{ t("annotation_visible_to_students_on_label") }}</template>
        </cv-toggle>
      </dd>
    </dl>
    <dl
      v-if="showMetadataControls && showNotifyStudents"
      class="field-set type-checkbox notify-students"
    >
      <dd class="field-input">
        <InputCheckbox
          id="notifyStudents"
          value="notifyStudents"
          :disabled="isNotifyDisabled || isInputDisabled"
          :selected-values="notifyStudentsChkboxValue"
          @change="handleNotifyStudentsChange"
        >
          {{ notifyStudentText }}
        </InputCheckbox>
      </dd>
    </dl>
    <dl class="field-set actions">
      <dd class="field-input">
        <cv-button
          v-if="inline || !(isActivityNote && isCreateMode)"
          class="cancel"
          kind="tertiary"
          size="small"
          @click="handleCancel"
          @mousedown.prevent
        >
          {{ t("button_cancel") }}
        </cv-button>
        <FetchDataButton
          class="save"
          :kind="dataSaveButton.kind"
          :size="dataSaveButton.size"
          :error="inline ? saveFailed : buttonState.error"
          :loading="inline ? saving : buttonState.loading"
          @click="handleSave"
          @reset-error-state="handleResetError"
        >
          {{ t("button_save") }}
        </FetchDataButton>
      </dd>
    </dl>
  </cv-form>
</template>

<script setup lang="ts">
import {
  ANNOTATION_POPUP_EVENT,
  ANNOTATION_TYPE,
  DRAFT_ANNOTATION_UUID,
} from "../service/annotation.constant";
import { getValueOrDefault, isEmpty, notNull } from "@utils/var.check";
import RandomUtils from "@utils/random.utils";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import { NAMESPACE } from "@modules/annotation/service/annotation.constant";
import { useStore } from "vuex";
import EditableTagListAutoComplete from "@plugins/common/components/EditableTagListAutoComplete.vue";
import RichtextUtils from "@plugins/richtext/utils/richtext.utils";
import RichTextEditor from "@plugins/richtext/components/RichTextEditor.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { useAnnotationNote } from "@modules/annotation/composables/annotation.note.ts";
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  Annotation,
  AnnotationContext,
  AnnotationEditorQuestion,
  AnnotationNoteSaveData,
} from "@modules/annotation/types.ts";
import { useMention } from "@plugins/richtext/plugins/mentions/composables/mention.ts";
import { useI18n } from "vue-i18n";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useAnnotationAccess } from "@modules/annotation/composables/use-annotation-access.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import Keywords from "@plugins/keywords/components/Keywords.vue";
import ContentMapKeywordApi from "@apis/content.map.keyword.api.ts";
import { ContentMapKeyword } from "@modules/content-map/content-map-types.ts";
import questionApi from "@apis/question.api.ts";

interface Keywords {
  name: string;
  label: string;
  value: string;
}

interface ButtonState {
  loading: boolean;
  error: boolean;
  errorMessage: string;
}
interface Execution {
  code?: string;
  data?: object;
}

interface Props {
  getMentionableTeacherList?: () => object;
  getMentionableStudentList?: () => object;
  annotation?: Pick<
    Annotation,
    "id" | "uuid" | "note" | "keywords" | "visibleByStudents" | "appFeatureId" | "creatorName"
  > | null;
  inline?: boolean;
  saving?: boolean;
  saveFailed?: boolean;
  question?: AnnotationEditorQuestion | null;
  isCreateMode?: boolean;
  annotationType?: string;
  initialOptionType?: string;
  includeBuddyListMentions?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  getMentionableTeacherList: () => ({}),
  getMentionableStudentList: () => ({}),
  annotation: null,
  question: null,
  isCreateMode: false,
  annotationType: "",
  initialOptionType: "",
  includeBuddyListMentions: false,
});

const emit = defineEmits<{
  save: [data: AnnotationNoteSaveData];
  cancel: [];
  resetError: [];
}>();

// Inject
const annotationContext = inject<AnnotationContext>("annotationContext")!;

const { isEditorContext } = useAnnotationPermissions(annotationContext);

const uuid = ref(
  props.annotation?.uuid && (!props.inline || props.annotation.uuid !== DRAFT_ANNOTATION_UUID)
    ? props.annotation.uuid
    : RandomUtils.randomUuid(),
);
const { mentionedTeachers, mentionedStudents } = useMention();
const { isDirty, setDirty, removeDirty, promptComponentDirty } = useDirty(uuid);
const { passiveApiFailHandler } = useErrorHandler();
const { trackWatch } = useEventBusWatch();
const { t } = useI18n();
const store = useStore();
const editor = ref({
  config: {},
  key: 0,
  kind: "student_teacher_tinymce_5",
  size: "large",
});
const rawNote = props.annotation?.note ?? "";
const note = ref(
  rawNote.includes("sls-algebra-kit-content")
    ? getValueOrDefault(rawNote.replaceAll("$$", "##"), "")
    : getValueOrDefault(rawNote, ""),
);
const hasContentMaps = ref(getValueOrDefault(props.question?.hasContentMaps, false));
const keywordsRef = ref(getValueOrDefault(props.annotation?.keywords, ""));
const buttonState = ref<ButtonState>({ loading: false, error: false, errorMessage: "" });
const notifyStudentsChkboxValue = ref<string[]>([]);
const resourceToBeMarked = ref(false);
const keywordsPlaceholder = ref(t("keywords_placeholder_label"));
const autoCompletePlaceholder = ref(t("autoComplete_placeholder_label"));
const task = ref("");
const execution = ref<Execution>({ code: "" });
const isAlgebraKitAnnotation = ref(false);
const keywordLists = ref<string[]>([]);
const visibleByStudentsToggleValue = ref(props.annotation?.visibleByStudents);
const initialOptionType = ref(props.initialOptionType);
const dataSaveButton = ref({
  kind: "primary",
  size: "small",
  error: false,
  loading: false,
});

// Computed
const userFormattedName = computed(() => store.getters[`${NAMESPACE.AUTH}/userFormattedName`]);
const userId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);
const isTeacher = computed(() => store.getters[`${NAMESPACE.AUTH}/isTeacher`]);

const currentUserId = computed(() => annotationContext.userId);
const isTeacherStudentView = computed(() => annotationContext.isTeacherStudentView);
const isTeacherView = computed(() => annotationContext.isTeacherView);
const isAttemptableSession = computed(() => annotationContext.isAttemptableSession);
const isFromAlsAdaptiveSection = computed(() => annotationContext.isFromAlsAdaptiveSection);
const academicYear = computed(() => annotationContext.academicYear);
const { showVisibleByStudentOptions, showNotifyStudents, disableNotifyStudents } =
  useAnnotationAccess({
    isTeacherView,
    isTeacherStudentView,
    isAttemptableSession,
    isFromAlsAdaptiveSection,
    resourceToBeMarked,
    visibleByStudents: visibleByStudentsToggleValue,
  });

const showMetadataControls = computed(() => !props.inline || !props.isCreateMode);
const isThirdPartyAnnotation = computed(() => Boolean(props.annotation?.appFeatureId));
const showSharingControls = computed(
  () =>
    showMetadataControls.value &&
    showVisibleByStudentOptions.value &&
    (!props.inline || !isThirdPartyAnnotation.value),
);
const isNotifyDisabled = computed(
  () => !(props.inline && isThirdPartyAnnotation.value) && disableNotifyStudents.value,
);

const getIntegratorAppAccessByAppFeatureId = computed(
  () => store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`],
);

const isInputDisabled = computed(() => props.saving || buttonState.value.loading);
const isActivityNote = computed(() => props.annotationType === ANNOTATION_TYPE.ACTIVITY_NOTE);

const showAutoCompleteKeywordTag = computed(() => hasContentMaps.value && isTeacher.value);
const notifyStudentText = computed(() =>
  showVisibleByStudentOptions.value
    ? t("annotation_notify_students_toggle_label")
    : t("notify_student_toggle_label"),
);
const lfaAppName = computed(
  () =>
    getIntegratorAppAccessByAppFeatureId.value(props.annotation?.appFeatureId)?.appFeatureName ??
    "",
);

const ownerName = computed(() => {
  if (isEmpty(props.annotation)) return userFormattedName.value;
  return isEmpty(props.annotation?.appFeatureId)
    ? (props.annotation?.creatorName ?? userFormattedName.value)
    : lfaAppName.value;
});

const parseKeywordsToSelection = (keywords: string): Keywords[] => {
  return keywords
    ? keywords
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean)
        .map((k) => ({ name: k, label: k, value: k }))
    : [];
};

const selectedItems = computed<Keywords[]>({
  get: () => parseKeywordsToSelection(keywordsRef.value),
  set: (val: Keywords[]) => {
    keywordsRef.value = val.map((item) => item.label).join(", ");
  },
});

watch(execution, (newExecution) => {
  if (Object.keys(newExecution).length < 1) return;
  if (newExecution.code === "error") {
    stopTaskExecution();
  } else {
    executeTask(newExecution);
  }
});

let unwatchDictionaryPopupState: () => void;
onBeforeUnmount(() => {
  if (unwatchDictionaryPopupState) {
    unwatchDictionaryPopupState();
  }
});

const setWatchStoreData = () => {
  // Close annotation popup when dictionary popup is opened
  unwatchDictionaryPopupState = store.watch(
    (state) => (state.annotation ? state.annotation.dictionaryPopupState : ""),
    (value) => {
      if (value) {
        if (!props.inline) AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_CANCEL, {});
      }
    },
  );
};

const executeTask = (executionData) => {
  handleKeywordsChange(executionData.data);
  handleSave();
};

const stopTaskExecution = () => {
  clearTaskExecution();
  buttonState.value.loading = false;
  buttonState.value.error = true;
  buttonState.value.errorMessage = "";
};

const handleResetError = (): void => {
  buttonState.value.error = false;
  if (props.inline) emit("resetError");
};

const clearTaskExecution = () => {
  task.value = "";
  execution.value = {};
};

const handleKeywordsChange = (tags: string) => {
  if (keywordsRef.value !== tags) {
    keywordsRef.value = tags;
    setDirty(uuid.value);
  }
};

const handleSave = () => {
  if (isInputDisabled.value) return;
  const currentKeywords: string = keywordsRef.value ?? props.annotation?.keywords ?? "";
  const mergedKeywords = Array.from(
    new Set(
      currentKeywords
        ? currentKeywords
            .split(",")
            .map((k: string) => k.trim())
            .filter((k: string) => k)
        : [],
    ),
  ).join(",");
  const data: AnnotationNoteSaveData = {
    updatedNotes: note.value ?? props.annotation?.note ?? "",
    isNotesDirty: isDirty(uuid.value),
    dirtyId: uuid.value,
    notifyStudents: !!notifyStudentsChkboxValue.value?.[0],
    keywords: mergedKeywords,
    visibleByStudents: visibleByStudentsToggleValue.value ?? false,
    mentionModel: {
      mentionedTeachers: mentionedTeachers.value,
      mentionedStudents: mentionedStudents.value,
      viewedUserId: currentUserId.value ?? userId.value,
    },
    academicYear: academicYear.value,
  };
  if (isAlgebraKitAnnotation.value) {
    data.updatedNotes = RichtextUtils.removeAlgebraKitScriptTags(note.value);
  }

  if (props.inline) {
    emit("save", data);
    return;
  }
  buttonState.value.loading = true;
  AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE, data);

  //reset notify students value
  notifyStudentsChkboxValue.value = [];
};

const handleNotesSavedSuccessfully = () => {
  buttonState.value.loading = false;
  clearTaskExecution();
  removeDirty(uuid.value);
};

const handleCancel = async (event?: Event): Promise<void> => {
  event?.preventDefault();
  if (isInputDisabled.value) return;
  if (!(await promptComponentDirty())) return;
  if (props.inline) {
    emit("cancel");
  } else {
    AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_CANCEL, {});
  }
};

const handleNotesChange = (newNotes: string) => {
  //Handle decoding for AlgebraKiT content
  if (newNotes.includes("data-spec")) {
    newNotes = newNotes.replaceAll("##", "$$$");
  }
  if (note.value !== newNotes) {
    setDirty(uuid.value);
    note.value = newNotes;
  }
};

const onFail = (error) => {
  buttonState.value.loading = false;
  passiveApiFailHandler(error);
};

const handleKeywordTagChange = (selection: Keywords[]) => {
  selectedItems.value = selection;
  setDirty(uuid.value);
};

const handleToggleVisibleByStudents = (toggleValue: boolean) => {
  visibleByStudentsToggleValue.value = toggleValue;
  setDirty(uuid.value);
};

const handleNotifyStudentsChange = (value: string[]) => {
  notifyStudentsChkboxValue.value = value;
  setDirty(uuid.value);
};

const getContext = () => {
  return new Promise((resolve) => {
    if (!isEmpty(props.annotation) && props.annotation?.uuid !== DRAFT_ANNOTATION_UUID) {
      // case when it is editing an existing annotation
      // the reference UUID would already be existing.
      resolve({
        resourceType: "ANNOTATION",
        resourceUuid: props.annotation?.uuid,
        resourceId: props.annotation?.id,
      });
      return;
    }

    if (notNull(uuid.value)) {
      // case when it is annotation creation and there is a preloaded UUID
      resolve({
        resourceType: "ANNOTATION",
        resourceUuid: uuid.value,
        resourceId: 0,
      });
      return;
    }
  });
};

const { richTextEditorConfig } = useAnnotationNote(
  {
    deactiveNoteToggle: undefined,
    handleNoteChangeConfirm: undefined,
    handleNoteChangeCancel: undefined,
    getContext: getContext,
    getMentionableTeacherList: props.getMentionableTeacherList,
    getMentionableStudentList: props.getMentionableStudentList,
    includeBuddyListMentions: props.includeBuddyListMentions,
    mode: "",
  },
  uuid,
);

const fetchKeywordList = async (questionId: number | undefined) => {
  if (!showAutoCompleteKeywordTag.value || !questionId) {
    keywordLists.value = [];
    return;
  }

  try {
    const response = await ContentMapKeywordApi.fetchContentMapKeywordByQuestionId(questionId);
    keywordLists.value = []; // Clear existing keywordLists
    response.data.forEach((contentMapKeyword: ContentMapKeyword) => {
      keywordLists.value.push(contentMapKeyword.keyword);
    });
  } catch (error) {
    passiveApiFailHandler(error);
  }
};

const checkIfQuestionHasContentMaps = async (questionId: number | undefined) => {
  if (!questionId) return;
  const contentMapResponse = await questionApi.getQuestionHasContentMaps(questionId);
  hasContentMaps.value =
    contentMapResponse.code === "ok" ? contentMapResponse.data.hasContentMaps : false;
};

onMounted(async () => {
  setWatchStoreData();
  if (!props.inline) {
    trackWatch(
      AnnotationEventBus.$on(
        ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE_SUCCESS,
        handleNotesSavedSuccessfully,
      ),
    );
    trackWatch(AnnotationEventBus.$on(ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE_FAIL, onFail));
  }
  if (props.inline) return;
  try {
    await checkIfQuestionHasContentMaps(props.question?.id);
    await fetchKeywordList(props.question?.id);
  } catch (error) {
    passiveApiFailHandler(error);
  }
});

onBeforeUnmount(() => {
  handleNotesSavedSuccessfully();
});

defineExpose({ handleCancel });
</script>
